import { useMemo, useState, useRef, useEffect } from "react";
import { applyClientOverrides } from "../utils/clientOverrides";
import { buildSnapshotReportHtml, EMPTY_SNAPSHOT } from "../utils/buildSnapshotReport";
import { useReportSendLog, wasSentThisMonth } from "../hooks/useReportSendLog";
import { useLogoStore } from "../hooks/useLogoStore";
import { useSettingsStore } from "../hooks/useSettingsStore";
import { supabase } from "../lib/supabase";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const sleep = (ms) => new Promise(r => setTimeout(r, ms));

// Bulk monthly send: emails every selected client their own snapshot report
// (PDF, no advisor recommendations) via POST /api/send-email, one at a time.
// Sequential on purpose — the server renders each PDF with Puppeteer, and
// Resend rate-limits at ~2 req/s; parallelism buys nothing and risks 429s.
export default function BulkReportSendModal({ clients, onClose, onLogActivity }) {
  const { logo } = useLogoStore();
  const { settings } = useSettingsStore();
  const { log, markSent } = useReportSendLog();

  const [tab, setTab] = useState("send"); // 'send' | 'history'
  const [histFilter, setHistFilter] = useState("");

  // status per clientId: undefined | 'sending' | 'sent' | 'failed'
  const [statuses, setStatuses] = useState({});
  const [errors, setErrors] = useState({});
  const [running, setRunning] = useState(false);
  const [done, setDone] = useState(false);
  const [fatal, setFatal] = useState("");
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const cancelRef = useRef(false);

  // Build the candidate rows once. The monthly report goes to ACTIVE clients
  // only — those under אחזקות — and is built from their holdings funds (the
  // live current state). טרום-ייעוץ / פרישה clients are excluded entirely.
  const rows = useMemo(() => {
    const withEmail = [];
    let skipped = 0;
    for (const c of clients || []) {
      const holdingsFunds = c.hasHoldings ? (c.holdings?.funds || []) : [];
      if (!holdingsFunds.length) continue;
      const merged = applyClientOverrides(holdingsFunds[0]?.client || null, c.clientId);
      const email = (merged?.email || "").trim();
      if (EMAIL_RE.test(email)) {
        withEmail.push({ id: c.clientId, name: c.clientName || c.fileInfo?.client || c.clientId, email, funds: holdingsFunds });
      } else {
        skipped++;
      }
    }
    return { withEmail, skipped };
  }, [clients]);

  // Default selection: everyone who was NOT sent this calendar month.
  const [selected, setSelected] = useState(() => {
    const s = new Set();
    return s; // filled in the effect below once rows are known
  });
  const [selectionInit, setSelectionInit] = useState(false);
  useEffect(() => {
    if (selectionInit) return;
    const s = new Set(rows.withEmail.filter(r => !wasSentThisMonth(log[r.id])).map(r => r.id));
    setSelected(s);
    setSelectionInit(true);
  }, [rows, log, selectionInit]);

  // Warn before closing the tab mid-run
  useEffect(() => {
    if (!running) return;
    const onBeforeUnload = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [running]);

  const toggle = (id) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const monthLabel = new Date().toLocaleDateString("he-IL", { month: "long", year: "numeric" });
  const subject = `דוח מצב פנסיוני — ${monthLabel}`;

  const runSend = async (targetIds) => {
    setRunning(true);
    setDone(false);
    setFatal("");
    cancelRef.current = false;
    const targets = rows.withEmail.filter(r => targetIds.has(r.id));
    setProgress({ current: 0, total: targets.length });

    let sentCount = 0;
    for (let i = 0; i < targets.length; i++) {
      if (cancelRef.current) break;
      const r = targets[i];
      setStatuses(prev => ({ ...prev, [r.id]: "sending" }));
      setProgress({ current: i + 1, total: targets.length });
      try {
        // Fresh token each iteration — a 100-client run takes ~10 minutes and
        // getSession() transparently refreshes an expiring JWT.
        const { data: { session } } = await supabase?.auth.getSession() ?? { data: {} };
        const jwt = session?.access_token;
        if (!jwt) throw new Error("נדרשת התחברות מחדש");

        // Build this client's HTML lazily — never hold 100 reports in memory.
        const html = buildSnapshotReportHtml({
          funds: r.funds, clientName: r.name, logo,
          snapshot: EMPTY_SNAPSHOT, hideRecommendations: true,
        });

        const resp = await fetch("/api/send-email", {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${jwt}` },
          body: JSON.stringify({
            to: r.email, subject, html,
            agentName: settings.licenseName || "",
            agentEmail: settings.licenseEmail || settings.contactEmail || "",
            clientName: r.name,
          }),
        });
        const json = await resp.json().catch(() => ({}));

        if (resp.status === 503 && json.error === "not_configured") {
          setStatuses(prev => ({ ...prev, [r.id]: "failed" }));
          setFatal("שליחת מייל אינה מוגדרת בשרת (חסר מפתח Resend) — הריצה נעצרה.");
          break;
        }
        if (resp.status === 429) {
          setStatuses(prev => ({ ...prev, [r.id]: "failed" }));
          setFatal("חריגה ממגבלת הקצב של השרת — ההתקדמות נשמרה, נסה שוב בעוד 15 דקות.");
          break;
        }
        if (!resp.ok) throw new Error(json.error || `שגיאת שרת ${resp.status}`);

        markSent(r.id, r.email, { clientName: r.name, kind: "bulk", subject, period: new Date().toISOString().slice(0, 7) });
        sentCount++;
        setStatuses(prev => ({ ...prev, [r.id]: "sent" }));
      } catch (err) {
        setStatuses(prev => ({ ...prev, [r.id]: "failed" }));
        setErrors(prev => ({ ...prev, [r.id]: err.message || "שגיאה" }));
      }
      await sleep(400); // pacing: Resend allows ~2 req/s; PDF render dominates anyway
    }

    setRunning(false);
    setDone(true);
    if (sentCount > 0 && onLogActivity) {
      onLogActivity({
        type: "report_sent",
        description: `שליחה מרוכזת: דוח תמונת מצב נשלח ל-${sentCount} לקוחות (${monthLabel})`,
        clientName: "הכל",
        date: new Date().toISOString().slice(0, 10),
      });
    }
  };

  const failedIds = useMemo(
    () => new Set(Object.entries(statuses).filter(([, s]) => s === "failed").map(([id]) => id)),
    [statuses]
  );
  const sentNow = Object.values(statuses).filter(s => s === "sent").length;

  const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("he-IL") : "—";
  const fmtDateTime = (iso) => iso
    ? `${new Date(iso).toLocaleDateString("he-IL")} ${new Date(iso).toLocaleTimeString("he-IL", { hour: "2-digit", minute: "2-digit" })}`
    : "—";

  // ── History rows: flatten per-client history across all clients ────────────
  const nameById = useMemo(
    () => new Map((clients || []).map(c => [c.clientId, c.clientName])),
    [clients]
  );
  const historyRows = useMemo(() => {
    const out = [];
    for (const [cid, entry] of Object.entries(log || {})) {
      const name = entry.clientName || nameById.get(cid) || cid;
      const hist = entry.history || [];
      for (const h of hist) out.push({ clientId: cid, name, ...h });
      // legacy entries recorded before history existed — synthesize their last send
      if (!hist.length && entry.lastSentAt) {
        out.push({ clientId: cid, name, sentAt: entry.lastSentAt, email: entry.email, kind: "bulk", subject: "" });
      }
    }
    out.sort((a, b) => (a.sentAt < b.sentAt ? 1 : -1));
    return out;
  }, [log, nameById]);

  const filteredHistory = useMemo(() => {
    const q = histFilter.trim().toLowerCase();
    if (!q) return historyRows;
    return historyRows.filter(h =>
      (h.name || "").toLowerCase().includes(q) || (h.email || "").toLowerCase().includes(q)
    );
  }, [historyRows, histFilter]);

  const historyClientCount = useMemo(
    () => new Set(historyRows.map(h => h.clientId)).size,
    [historyRows]
  );

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000,
      display: "flex", alignItems: "flex-start", justifyContent: "center",
      padding: "24px 16px", overflowY: "auto", direction: "rtl",
    }} onClick={running ? undefined : onClose}>
      <div style={{
        background: "#f8fafc", borderRadius: 16, width: "100%", maxWidth: 760,
        boxShadow: "0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -6px rgba(0,0,0,0.1)",
        overflow: "hidden",
      }} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{
          background: "linear-gradient(135deg, #0e7490, #0e7490dd)", padding: "16px 24px",
          color: "white", display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
          <div>
            <div style={{ fontSize: 20, fontWeight: 800 }}>📤 דוח חודשי לכל הלקוחות</div>
            <div style={{ fontSize: 12, opacity: 0.85, marginTop: 2 }}>
              {subject} · לקוחות אחזקות בלבד · ללא המלצות יועץ · PDF אישי לכל לקוח
            </div>
          </div>
          {!running && (
            <button onClick={onClose} style={{
              background: "rgba(255,255,255,0.2)", border: "1px solid rgba(255,255,255,0.4)",
              color: "white", padding: "6px 12px", borderRadius: 8, cursor: "pointer", fontSize: 14,
            }}>✕</button>
          )}
        </div>

        {/* Tab bar */}
        <div style={{ display: "flex", gap: 0, borderBottom: "1px solid #e2e8f0", background: "white" }}>
          {[["send", "📤 שליחה"], ["history", "🕐 היסטוריה"]].map(([key, label]) => (
            <button key={key} onClick={() => !running && setTab(key)} style={{
              padding: "10px 22px", fontSize: 13, fontWeight: 700, cursor: running ? "default" : "pointer",
              background: "transparent", border: "none",
              color: tab === key ? "#0e7490" : "#94a3b8",
              borderBottom: tab === key ? "2.5px solid #0e7490" : "2.5px solid transparent",
            }}>
              {label}
            </button>
          ))}
        </div>

        {tab === "history" && (
          <div style={{ padding: 20 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
              <div style={{ fontSize: 13, color: "#334155", fontWeight: 700 }}>
                {historyRows.length} שליחות ל-{historyClientCount} לקוחות
              </div>
              <input
                value={histFilter}
                onChange={e => setHistFilter(e.target.value)}
                placeholder="🔍 סינון לפי שם / מייל..."
                style={{ padding: "6px 10px", border: "1px solid #cbd5e1", borderRadius: 7, fontSize: 13, width: 220 }}
              />
            </div>
            <div style={{ maxHeight: 420, overflowY: "auto", border: "1px solid #e2e8f0", borderRadius: 8, background: "white" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ background: "#f1f5f9", position: "sticky", top: 0 }}>
                    <th style={{ padding: "8px 10px", textAlign: "right", fontWeight: 700, color: "#475569" }}>תאריך ושעה</th>
                    <th style={{ padding: "8px 10px", textAlign: "right", fontWeight: 700, color: "#475569" }}>לקוח</th>
                    <th style={{ padding: "8px 10px", textAlign: "right", fontWeight: 700, color: "#475569" }}>מייל</th>
                    <th style={{ padding: "8px 10px", textAlign: "right", fontWeight: 700, color: "#475569" }}>דוח</th>
                    <th style={{ padding: "8px 10px", textAlign: "center", fontWeight: 700, color: "#475569", width: 70 }}>סוג</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredHistory.map((h, i) => (
                    <tr key={`${h.clientId}_${h.sentAt}_${i}`} style={{ borderTop: "1px solid #f1f5f9" }}>
                      <td style={{ padding: "6px 10px", color: "#475569", whiteSpace: "nowrap" }}>{fmtDateTime(h.sentAt)}</td>
                      <td style={{ padding: "6px 10px", fontWeight: 600, color: "#0f172a" }}>{h.name}</td>
                      <td style={{ padding: "6px 10px", direction: "ltr", textAlign: "right", color: "#475569" }}>{h.email}</td>
                      <td style={{ padding: "6px 10px", color: "#64748b" }}>{h.subject || "דוח מצב פנסיוני"}</td>
                      <td style={{ padding: "6px 10px", textAlign: "center" }}>
                        <span style={{
                          fontSize: 10, fontWeight: 700, borderRadius: 8, padding: "2px 8px",
                          background: h.kind === "single" ? "#ede9fe" : "#cffafe",
                          color: h.kind === "single" ? "#7c3aed" : "#0e7490",
                        }}>
                          {h.kind === "single" ? "בודדת" : "מרוכזת"}
                        </span>
                      </td>
                    </tr>
                  ))}
                  {filteredHistory.length === 0 && (
                    <tr><td colSpan={5} style={{ padding: 20, textAlign: "center", color: "#94a3b8" }}>
                      {historyRows.length === 0 ? "עוד לא נשלחו דוחות. אחרי השליחה הראשונה כל שליחה תתועד כאן." : "אין תוצאות לסינון."}
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "send" && (
        <div style={{ padding: 20 }}>
          {/* Summary + controls */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
            <div style={{ fontSize: 13, color: "#334155", fontWeight: 700 }}>
              נבחרו {selected.size} מתוך {rows.withEmail.length} לקוחות אחזקות עם מייל
              {rows.skipped > 0 && (
                <span style={{ fontWeight: 400, color: "#94a3b8" }}> · {rows.skipped} לקוחות אחזקות ללא מייל תקין לא מוצגים</span>
              )}
            </div>
            {!running && !done && (
              <div style={{ display: "flex", gap: 6 }}>
                <button onClick={() => setSelected(new Set(rows.withEmail.map(r => r.id)))}
                  style={{ fontSize: 12, padding: "4px 10px", borderRadius: 6, border: "1px solid #cbd5e1", background: "white", cursor: "pointer" }}>
                  בחר הכל
                </button>
                <button onClick={() => setSelected(new Set())}
                  style={{ fontSize: 12, padding: "4px 10px", borderRadius: 6, border: "1px solid #cbd5e1", background: "white", cursor: "pointer" }}>
                  נקה בחירה
                </button>
              </div>
            )}
          </div>

          {selected.size > 100 && !running && (
            <div style={{ background: "#fef3c7", border: "1px solid #fcd34d", borderRadius: 8, padding: "8px 12px", fontSize: 12, color: "#92400e", marginBottom: 12 }}>
              ⚠️ נבחרו מעל 100 נמענים — בתוכנית החינמית של Resend המכסה היא 100 מיילים ביום.
              אם הריצה תיעצר, ההתקדמות נשמרת ואפשר להשלים מחר דרך "שלח לנכשלים".
            </div>
          )}

          {fatal && (
            <div style={{ background: "#fee2e2", border: "1px solid #fca5a5", borderRadius: 8, padding: "8px 12px", fontSize: 13, color: "#b91c1c", marginBottom: 12, fontWeight: 600 }}>
              {fatal}
            </div>
          )}

          {/* Progress bar */}
          {(running || done) && progress.total > 0 && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "#475569", marginBottom: 4 }}>
                <span>{running ? `שולח ${progress.current} מתוך ${progress.total}…` : `הסתיים: ${sentNow} נשלחו, ${failedIds.size} נכשלו`}</span>
                <span>{Math.round((progress.current / progress.total) * 100)}%</span>
              </div>
              <div style={{ height: 8, background: "#e2e8f0", borderRadius: 4, overflow: "hidden" }}>
                <div style={{
                  height: "100%", width: `${(progress.current / progress.total) * 100}%`,
                  background: fatal ? "#f59e0b" : "#0e7490", transition: "width 0.3s ease",
                }} />
              </div>
            </div>
          )}

          {/* Client table */}
          <div style={{ maxHeight: 380, overflowY: "auto", border: "1px solid #e2e8f0", borderRadius: 8, background: "white" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "#f1f5f9", position: "sticky", top: 0 }}>
                  <th style={{ padding: "8px 10px", width: 34 }} />
                  <th style={{ padding: "8px 10px", textAlign: "right", fontWeight: 700, color: "#475569" }}>לקוח</th>
                  <th style={{ padding: "8px 10px", textAlign: "right", fontWeight: 700, color: "#475569" }}>מייל</th>
                  <th style={{ padding: "8px 10px", textAlign: "right", fontWeight: 700, color: "#475569" }}>נשלח לאחרונה</th>
                  <th style={{ padding: "8px 10px", textAlign: "center", fontWeight: 700, color: "#475569", width: 70 }}>סטטוס</th>
                </tr>
              </thead>
              <tbody>
                {rows.withEmail.map(r => {
                  const entry = log[r.id];
                  const sentThisMonth = wasSentThisMonth(entry);
                  const st = statuses[r.id];
                  return (
                    <tr key={r.id} style={{ borderTop: "1px solid #f1f5f9", opacity: st === "sent" ? 0.75 : 1 }}>
                      <td style={{ padding: "6px 10px", textAlign: "center" }}>
                        <input
                          type="checkbox"
                          checked={selected.has(r.id)}
                          disabled={running}
                          onChange={() => toggle(r.id)}
                        />
                      </td>
                      <td style={{ padding: "6px 10px", fontWeight: 600, color: "#0f172a" }}>
                        {r.name}
                        {sentThisMonth && !st && (
                          <span style={{ marginRight: 6, fontSize: 10, background: "#dcfce7", color: "#166534", borderRadius: 8, padding: "1px 7px", fontWeight: 700 }}>
                            נשלח החודש
                          </span>
                        )}
                      </td>
                      <td style={{ padding: "6px 10px", direction: "ltr", textAlign: "right", color: "#475569" }}>{r.email}</td>
                      <td style={{ padding: "6px 10px", color: "#64748b" }}>{fmtDate(entry?.lastSentAt)}</td>
                      <td style={{ padding: "6px 10px", textAlign: "center" }} title={errors[r.id] || ""}>
                        {st === "sending" ? "⏳" : st === "sent" ? "✅" : st === "failed" ? "❌" : ""}
                      </td>
                    </tr>
                  );
                })}
                {rows.withEmail.length === 0 && (
                  <tr><td colSpan={5} style={{ padding: 20, textAlign: "center", color: "#94a3b8" }}>
                    אין לקוחות אחזקות עם כתובת מייל. הוסף מייל ללקוח דרך "פרטי לקוח" ← עריכה.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Footer actions */}
          <div style={{ display: "flex", justifyContent: "flex-start", gap: 10, marginTop: 16, alignItems: "center" }}>
            {!running && !done && (
              <button
                onClick={() => runSend(selected)}
                disabled={selected.size === 0}
                style={{
                  background: selected.size ? "#0e7490" : "#cbd5e1", color: "white", border: "none",
                  padding: "10px 22px", borderRadius: 8, cursor: selected.size ? "pointer" : "default",
                  fontWeight: 700, fontSize: 14,
                }}>
                📤 שלח ל-{selected.size} לקוחות
              </button>
            )}
            {running && (
              <button onClick={() => { cancelRef.current = true; }}
                style={{ background: "#dc2626", color: "white", border: "none", padding: "10px 22px", borderRadius: 8, cursor: "pointer", fontWeight: 700, fontSize: 14 }}>
                ⏹ עצור אחרי הלקוח הנוכחי
              </button>
            )}
            {done && failedIds.size > 0 && !fatal.includes("Resend") && (
              <button onClick={() => runSend(failedIds)}
                style={{ background: "#d97706", color: "white", border: "none", padding: "10px 22px", borderRadius: 8, cursor: "pointer", fontWeight: 700, fontSize: 14 }}>
                🔁 שלח שוב לנכשלים ({failedIds.size})
              </button>
            )}
            {done && (
              <button onClick={onClose}
                style={{ background: "white", color: "#334155", border: "1px solid #cbd5e1", padding: "10px 22px", borderRadius: 8, cursor: "pointer", fontWeight: 700, fontSize: 14 }}>
                סגור
              </button>
            )}
            {running && (
              <span style={{ fontSize: 12, color: "#94a3b8" }}>אל תסגור את הטאב בזמן השליחה</span>
            )}
          </div>
        </div>
        )}
      </div>
    </div>
  );
}
